import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Card,
  CardBody,
  Heading,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getOrganizationSurveys } from "./organizationSurveysApi";
import { formatOrganizationSurveyTitle } from "../../utils/organizationSurveyHelper";

const OrganizationResultsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    const loadSurveys = async () => {
      try {
        setLoading(true);
        const nextSurveys = await getOrganizationSurveys();
        if (!active) return;
        setSurveys(nextSurveys);
      } catch (err) {
        if (!active) return;
        setError(t("organization.results_error", "Could not load school results."));
      } finally {
        if (active) setLoading(false);
      }
    };

    loadSurveys();

    return () => {
      active = false;
    };
  }, [t]);

  if (loading) {
    return (
      <Box py={10} textAlign="center">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      <Box>
        <Heading size="lg">{t("organization.results", "Results")}</Heading>
        <Text color="gray.600" mt={2}>
          {t(
            "organization.results_description",
            "Choose a survey cycle to open the full report inside the App shell."
          )}
        </Text>
      </Box>

      {surveys.length === 0 ? (
        <Card>
          <CardBody>
            <Text color="gray.600">
              {t(
                "organization.results_empty",
                "Results will appear here once your school has a survey cycle available."
              )}
            </Text>
          </CardBody>
        </Card>
      ) : (
        <VStack align="stretch" spacing={4}>
          {surveys.map((survey) => (
            <Card key={survey.organization_survey_id}>
              <CardBody>
                <VStack align="start" spacing={3}>
                  <Heading size="md">{formatOrganizationSurveyTitle(survey)}</Heading>
                  <Text color="gray.600">
                    {t(
                      "organization.results_cycle_copy",
                      "Open the full report to review overview, strengths, development, and supporting sections."
                    )}
                  </Text>
                  <Button
                    colorScheme="blue"
                    variant="outline"
                    onClick={() =>
                      navigate(`/app/organization/results/${survey.organization_survey_id}`, {
                        state: {
                          surveyTitle: formatOrganizationSurveyTitle(survey),
                          survey,
                        },
                      })
                    }
                  >
                    {t("organization.view_full_results", "View full results")}
                  </Button>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </VStack>
      )}
    </VStack>
  );
};

export default OrganizationResultsPage;
